import Radio from '@mui/material/Radio'
import { styled } from '@mui/material/styles'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import React from 'react'
import { useSelector } from 'react-redux'

import AudioPlayer from '@/app/component/atom/audio-player'
import TableRowActions from '@/app/component/layout/table/actions'
import { selectOrgId } from '@/app/module/user/store/selectors'
import downloadService from '@/app/service/util/download'

import type { AudioTableFile } from './audio-file-table'

type Props = {
  file: AudioTableFile
  selected?: boolean
  selectionName?: string

  onRemove?: (filename: string) => void
  onSelect?: (filename: string) => void
}

const AudioFileTableRow: React.FC<Props> = ({ file, selected, selectionName, onRemove, onSelect }) => {
  const orgId = useSelector(selectOrgId)
  const [audioFile, setAudioFile] = React.useState<string>()
  const [loading, setLoading] = React.useState(false)
  const transcript = file.transcription || ''

  const handlePlayToggle = React.useCallback(async () => {
    if (audioFile || loading) return
    setLoading(true)
    try {
      const audio = await downloadService.files(orgId, file.filename, false)
      const audioUrl = URL.createObjectURL(audio)
      setAudioFile(audioUrl)
    } finally {
      setLoading(false)
    }
  }, [audioFile, file.filename, loading, orgId])

  return (
    <TableRow
      hover
      data-testid="audio-file-table-row"
      onClick={onSelect ? () => onSelect(file.filename) : undefined}
      selected={!!selected}
      sx={{ cursor: onSelect ? 'pointer' : 'default' }}
    >
      {onSelect ? (
        <TableCell padding="checkbox">
          <Radio
            color="primary"
            checked={!!selected}
            inputProps={{ 'aria-label': `select-audio-${file.filename}` }}
            name={selectionName}
            value={file.filename}
            onChange={() => onSelect(file.filename)}
            onClick={(event) => event.stopPropagation()}
          />
        </TableCell>
      ) : null}
      <TableCell data-testid="audio-file-name" sx={{ overflowWrap: 'anywhere' }}>
        {file.title || file.filename}
      </TableCell>
      <TableCell data-testid="audio-player">
        <AudioPlayer loading={loading} playId={file.filename} source={audioFile} onPlayToggle={handlePlayToggle} />
      </TableCell>
      <TableCell data-testid="transcript-cell">
        {transcript ? (
          <Tooltip title={transcript} placement="top-start" enterDelay={300} arrow>
            <TranscriptContainer>
              <TranscriptText data-testid="transcript-full">{transcript}</TranscriptText>
            </TranscriptContainer>
          </Tooltip>
        ) : (
          <NoTranscript>No transcript</NoTranscript>
        )}
      </TableCell>
      {onRemove && (
        <TableCell align="center" onClick={(event) => event.stopPropagation()}>
          <ActionsContainer>
            <TableRowActions
              config={[
                {
                  disabled: !file.removable,
                  icon: 'delete',
                  label: 'Remove',
                  tooltip: file.removable
                    ? 'Remove from this campaign'
                    : 'This audio file is currently in use. Before you can proceed you must remove any usage of this file in the campaign.',
                  onClick: () => onRemove(file.filename),
                },
              ]}
            />
          </ActionsContainer>
        </TableCell>
      )}
    </TableRow>
  )
}

const ActionsContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  '& > div': {
    marginLeft: 0,
    marginRight: 0,
  },
})

const TranscriptContainer = styled('div')(({ theme }) => ({
  borderRadius: 4,
  padding: theme.spacing(1),
  fontSize: '0.75rem',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
}))

const TranscriptText = styled('div')({
  fontSize: '0.75rem',
  lineHeight: 1.4,
  display: '-webkit-box',
  WebkitLineClamp: 4,
  WebkitBoxOrient: 'vertical' as any,
  overflow: 'hidden',
})

const NoTranscript = styled('span')(({ theme }) => ({
  fontSize: '0.75rem',
  fontStyle: 'italic',
  color: theme.palette.text.disabled,
}))

export default AudioFileTableRow
